// src/utils/migrateToIndexedDB.js
// Migration utility to move existing albums from localStorage to IndexedDB
// Run this once when users first upgrade to the new version

import React from 'react';
import { saveAlbum, storePhotosBatch, listAlbums } from './photoStore';

// Keys used by the old localStorage system
const ALBUM_LIBRARY_INDEX_KEY = 'vvd:album:list';
const ALBUM_ITEM_PREFIX = 'vvd:album:item:';
const LEGACY_CACHE_KEY = 'vvd:lastAlbumPages';

/**
 * Check if migration is needed
 * DISABLED: No migration needed for fresh launch
 */
export async function needsMigration() {
  // Always return false - no users on old storage system
  return false;
}

/**
 * Get count of albums to migrate
 */
export function getMigrationCount() {
  try {
    const oldIndex = localStorage.getItem(ALBUM_LIBRARY_INDEX_KEY);
    if (!oldIndex) return 0;
    
    const parsed = JSON.parse(oldIndex);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

/**
 * Migrate all albums from localStorage to IndexedDB
 * @param {Function} onProgress - Progress callback ({ current, total, albumTitle, phase })
 * @returns {Promise<{ migrated: number, failed: number, errors: string[] }>}
 */
export async function migrateLocalStorageToIndexedDB(onProgress) {
  const result = {
    migrated: 0,
    failed: 0,
    errors: [],
    skipped: 0
  };
  
  try {
    console.log('[Migration] 🚀 Starting migration from localStorage to IndexedDB...');
    const startTime = Date.now();
    
    // Get album index
    const indexRaw = localStorage.getItem(ALBUM_LIBRARY_INDEX_KEY);
    if (!indexRaw) {
      console.log('[Migration] No albums to migrate');
      return result;
    }
    
    const index = JSON.parse(indexRaw);
    if (!Array.isArray(index) || index.length === 0) {
      console.log('[Migration] Empty album index');
      return result;
    }
    
    console.log(`[Migration] Found ${index.length} albums to migrate`);
    
    // Get existing albums in IndexedDB to avoid duplicates
    const existingAlbums = await listAlbums();
    const existingIds = new Set(existingAlbums.map(a => a.id));
    
    // Migrate each album
    for (let i = 0; i < index.length; i++) {
      const albumMeta = index[i];
      const albumKey = `${ALBUM_ITEM_PREFIX}${albumMeta.id}`;
      
      onProgress?.({
        current: i + 1,
        total: index.length,
        albumTitle: albumMeta.title || albumMeta.tripName || 'Untitled',
        phase: 'loading'
      });
      
      // Skip if already migrated
      if (existingIds.has(albumMeta.id)) {
        console.log(`[Migration] Skipping already migrated: ${albumMeta.id}`);
        result.skipped++;
        continue;
      }
      
      // Get album data
      const albumRaw = localStorage.getItem(albumKey);
      if (!albumRaw) {
        console.warn(`[Migration] No data for album: ${albumMeta.id}`);
        result.skipped++;
        continue;
      }
      
      try {
        const album = JSON.parse(albumRaw);
        
        onProgress?.({
          current: i + 1,
          total: index.length,
          albumTitle: album.title || album.tripName || 'Untitled',
          phase: 'extracting'
        });
        
        // Extract all photos from pages
        const allPhotos = [];
        const seenUrls = new Set();
        
        (album.pages || []).forEach(page => {
          // Photos array
          (page.photos || []).forEach(photo => {
            const url = photo.url || photo.src;
            if (url && url.startsWith('data:') && !seenUrls.has(url)) {
              seenUrls.add(url);
              allPhotos.push({
                url,
                gps: photo.gps || photo.coordinates,
                timestamp: photo.timestamp || photo.dateTaken,
                fileName: photo.fileName
              });
            }
          });
          
          // Items array
          (page.items || []).forEach(item => {
            if (item.url && item.url.startsWith('data:') && !seenUrls.has(item.url)) {
              seenUrls.add(item.url);
              allPhotos.push({
                url: item.url,
                gps: item.gps,
                timestamp: item.timestamp,
                fileName: item.fileName
              });
            }
          });
        });
        
        console.log(`[Migration] Album ${albumMeta.id}: ${allPhotos.length} photos`);
        
        // Store photos in IndexedDB
        onProgress?.({
          current: i + 1,
          total: index.length,
          albumTitle: album.title || album.tripName || 'Untitled',
          phase: 'storing',
          photoCount: allPhotos.length
        });
        
        if (allPhotos.length > 0) {
          await storePhotosBatch(allPhotos, albumMeta.id, (done, total) => {
            onProgress?.({
              current: i + 1,
              total: index.length,
              albumTitle: album.title || album.tripName || 'Untitled',
              phase: 'storing',
              photoProgress: { done, total }
            });
          });
        }
        
        // Save album metadata
        await saveAlbum(albumMeta.id, album);
        
        // Remove from localStorage (cleanup)
        localStorage.removeItem(albumKey);
        
        result.migrated++;
        console.log(`[Migration] ✅ Migrated: ${albumMeta.id}`);
        
      } catch (err) {
        console.error(`[Migration] ❌ Failed to migrate album ${albumMeta.id}:`, err);
        result.failed++;
        result.errors.push(`${albumMeta.id}: ${err.message}`);
      }
      
      // Yield to prevent UI freeze
      await new Promise(r => setTimeout(r, 10));
    }
    
    // Clean up localStorage index after successful migration
    if (result.migrated > 0 && result.failed === 0) {
      localStorage.removeItem(ALBUM_LIBRARY_INDEX_KEY);
      console.log('[Migration] Cleaned up localStorage index');
    }
    
    // Clean up legacy cache
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
      // Also clean up trip-specific caches
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('vvd:albumCache:')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {}
    
    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
    console.log(`[Migration] ✅ Complete: ${result.migrated} migrated, ${result.failed} failed, ${result.skipped} skipped in ${elapsed}s`);
    
    return result;
  } catch (err) {
    console.error('[Migration] Fatal error:', err);
    result.errors.push(`Fatal: ${err.message}`);
    return result;
  }
}

/**
 * Estimate localStorage usage
 */
export function getLocalStorageUsage() {
  let total = 0;
  let albumsSize = 0;
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      const size = (key?.length || 0) + (value?.length || 0);
      total += size;
      
      if (key?.startsWith('vvd:album')) {
        albumsSize += size;
      }
    }
  } catch {}
  
  return {
    totalBytes: total,
    totalMB: (total / (1024 * 1024)).toFixed(2),
    albumsBytes: albumsSize,
    albumsMB: (albumsSize / (1024 * 1024)).toFixed(2)
  };
}

/**
 * React component for migration UI
 */
export function MigrationBanner({ onMigrationComplete }) {
  const [needed, setNeeded] = React.useState(false);
  const [migrating, setMigrating] = React.useState(false);
  const [progress, setProgress] = React.useState(null);
  const [result, setResult] = React.useState(null);
  
  React.useEffect(() => {
    needsMigration().then(setNeeded);
  }, []);
  
  const handleMigrate = async () => {
    setMigrating(true);
    
    try {
      const res = await migrateLocalStorageToIndexedDB(setProgress);
      setResult(res);
      setNeeded(false);
      onMigrationComplete?.(res);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setMigrating(false);
    }
  };
  
  if (!needed && !migrating && !result) return null;
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      left: 20,
      right: 20,
      maxWidth: 400,
      margin: '0 auto',
      background: '#1e293b',
      color: 'white',
      padding: 16,
      borderRadius: 12,
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      zIndex: 9999
    }}>
      {result ? (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {result.error ? '❌ Migration Failed' : '✅ Migration Complete'}
          </div>
          {result.error ? (
            <div style={{ fontSize: 14, color: '#f87171' }}>{result.error}</div>
          ) : (
            <div style={{ fontSize: 14, color: '#94a3b8' }}>
              {result.migrated} albums migrated successfully
              {result.failed > 0 && `, ${result.failed} failed`}
            </div>
          )}
          <button
            onClick={() => setResult(null)}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      ) : migrating ? (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Migrating Albums...
          </div>
          {progress && (
            <div>
              <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 8 }}>
                {progress.albumTitle} ({progress.current}/{progress.total})
              </div>
              {progress.photoProgress && (
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Photos: {progress.photoProgress.done}/{progress.photoProgress.total}
                </div>
              )}
              <div style={{
                height: 4,
                background: '#334155',
                borderRadius: 2,
                marginTop: 8,
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${(progress.current / progress.total) * 100}%`,
                  background: '#3b82f6',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            📦 Album Migration Available
          </div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            Migrate your albums to the new storage system for better performance with large albums.
          </div>
          <button
            onClick={handleMigrate}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Migrate Now
          </button>
        </div>
      )}
    </div>
  );
}

export default migrateLocalStorageToIndexedDB;